<template>
    <Head title="Incident Status Report" />

    <div class="max-w-7xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
        <!-- Page Header -->
        <div class="flex items-center justify-between">
            <div>
                <Link :href="route('reports.incidents.index')" class="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mb-2 inline-flex items-center">
                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Reports
                </Link>
                <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Incident Status Report</h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Track incident and near miss statuses including triage, investigation progress, SLA compliance, and resolution details
                </p>
            </div>
        </div>

        <!-- Filters Section -->
        <div>
            <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Filters</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                    <Select v-model="filters.status" :options="statusOptions" multiple placeholder="All Statuses" />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Severity</label>
                    <Select v-model="filters.severity" :options="severityOptions" multiple placeholder="All Severities" />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Priority</label>
                    <Select v-model="filters.priority" :options="priorityOptions" multiple placeholder="All Priorities" />
                </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Location</label>
                    <Select v-model="filters.location" :options="locationOptions" multiple placeholder="All Locations" />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Incident Type</label>
                    <Select v-model="filters.type" :options="typeOptions" multiple placeholder="All Types" />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Report Type</label>
                    <Select v-model="filters.is_near_miss" :options="reportTypeOptions" placeholder="All Incidents & Near Misses" />
                </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div class="flex items-end">
                    <label class="flex items-center">
                        <input type="checkbox" v-model="filters.sla_breached" class="rounded border-gray-300 dark:border-gray-600 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 dark:focus:ring-blue-900 focus:ring-opacity-50" />
                        <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">SLA Breached Only</span>
                    </label>
                </div>
            </div>
        </div>

        <!-- Actions Toolbar -->
        <div class="flex items-center justify-between">
            <Button @click="generateReport" :disabled="loading">
                <span v-if="!loading">Generate Report</span>
                <span v-else class="flex items-center">
                    <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                </span>
            </Button>

            <div v-if="reportData.length > 0 && canExport" class="flex gap-2">
                <Button variant="secondary" @click="exportPdf">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export PDF
                </Button>
                <Button variant="secondary" @click="exportCsv">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export CSV
                </Button>
            </div>
        </div>

        <!-- Report Results -->
        <Card v-if="reportData.length > 0">
            <div class="p-6">
                <div class="flex items-center justify-between mb-4">
                    <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Report Results</h2>
                    <span class="text-sm text-gray-600 dark:text-gray-400">{{ reportData.length }} incidents</span>
                </div>

                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead class="bg-gray-50 dark:bg-gray-800">
                            <tr>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Incident #</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Location</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Severity</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priority</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reported By</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reported At</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Investigator</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Quick Action</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Corrective Action</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">SLA</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Age (Days)</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-for="(row, index) in reportData" :key="index">
                                <td class="px-6 py-4 whitespace-nowrap text-sm">
                                    <div class="flex flex-col">
                                        <span class="text-gray-900 dark:text-white font-medium">{{ row.incident_number }}</span>
                                        <span v-if="row.is_near_miss" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 mt-1">
                                            Near Miss
                                        </span>
                                        <span v-else class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 mt-1">
                                            Incident
                                        </span>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.type }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.location }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm">
                                    <span :class="getSeverityClass(row.severity)">{{ row.severity }}</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm">
                                    <span :class="getPriorityClass(row.priority)">{{ row.priority }}</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.reported_by }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.reported_at }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.investigator }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.quick_action_assignee }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.corrective_action_assignee }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm">
                                    <span :class="getStatusClass(row.status_raw)">{{ row.status }}</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm">
                                    <span v-if="row.sla_response_breached || row.sla_investigation_breached" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                                        Breached
                                    </span>
                                    <span v-else class="text-gray-400 dark:text-gray-500">-</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.age_days }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </Card>

        <!-- Empty State -->
        <Card v-else-if="!loading">
            <div class="p-12 text-center">
                <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p class="text-sm text-gray-500 dark:text-gray-400">Select filters and click "Generate Report" to view data</p>
            </div>
        </Card>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, usePage } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Select from '@/Components/Common/Select.vue';
import route from '@/Utils/route';
import { useToast } from '@/Composables/useToast';
import { useSecureAxios } from '@/Composables/useSecureAxios';

const toast = useToast();
const axios = useSecureAxios();
const page = usePage();

// Subscription features
const subscriptionFeatures = computed(() => page.props.subscriptionFeatures || { asset_module: false, equipment_module: false });

const props = defineProps({
    filters: Object,
    subscriptionFeatures: Object,
    canExport: Boolean,
});

// State
const loading = ref(false);
const reportData = ref([]);
const filters = ref({
    status: [],
    severity: [],
    priority: [],
    location: [],
    type: [],
    is_near_miss: null,
    sla_breached: false,
});

// Computed
const statusOptions = computed(() => {
    return props.filters.statuses.map(s => ({
        value: s,
        label: s.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    }));
});

const severityOptions = computed(() => {
    return props.filters.severities.map(s => ({
        value: s,
        label: s.charAt(0).toUpperCase() + s.slice(1)
    }));
});

const priorityOptions = computed(() => {
    return props.filters.priorities.map(p => ({
        value: p,
        label: p.charAt(0).toUpperCase() + p.slice(1)
    }));
});

const locationOptions = computed(() => {
    return props.filters.locations.map(l => ({ value: l.id, label: l.name }));
});

const typeOptions = computed(() => {
    return props.filters.incidentTypes.map(t => ({ value: t.id, label: t.name }));
});

const reportTypeOptions = [
    { value: null, label: 'All Incidents & Near Misses' },
    { value: 'false', label: 'Incidents Only' },
    { value: 'true', label: 'Near Misses Only' },
];

// Methods
const generateReport = async () => {
    loading.value = true;

    try {
        const response = await axios.get(route('reports.incidents.incident-status.data'), {
            params: filters.value
        });

        if (response.data && response.data.data) {
            reportData.value = response.data.data;
            toast.success(`Generated ${response.data.total} incident records`);
        }
    } catch (error) {
        toast.error('Failed to generate report');
        console.error(error);
    } finally {
        loading.value = false;
    }
};

const exportPdf = () => {
    const queryParams = new URLSearchParams();
    Object.keys(filters.value).forEach(key => {
        if (filters.value[key] !== null && filters.value[key] !== '' && filters.value[key] !== false) {
            if (Array.isArray(filters.value[key])) {
                filters.value[key].forEach(val => queryParams.append(`${key}[]`, val));
            } else {
                queryParams.append(key, filters.value[key]);
            }
        }
    });

    window.open(route('reports.incidents.incident-status.pdf') + '?' + queryParams.toString(), '_blank');
    toast.success('Generating PDF...');
};

const exportCsv = () => {
    const queryParams = new URLSearchParams();
    Object.keys(filters.value).forEach(key => {
        if (filters.value[key] !== null && filters.value[key] !== '' && filters.value[key] !== false) {
            if (Array.isArray(filters.value[key])) {
                filters.value[key].forEach(val => queryParams.append(`${key}[]`, val));
            } else {
                queryParams.append(key, filters.value[key]);
            }
        }
    });

    window.open(route('reports.incidents.incident-status.csv') + '?' + queryParams.toString(), '_blank');
    toast.success('Generating CSV...');
};

// Utility classes
const getSeverityClass = (severity) => {
    const lowerSeverity = severity?.toLowerCase();
    const classes = {
        'low': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        'medium': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        'high': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        'critical': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    return classes[lowerSeverity] || 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};

const getPriorityClass = (priority) => {
    const lowerPriority = priority?.toLowerCase();
    const classes = {
        'low': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        'normal': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        'high': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        'urgent': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    return classes[lowerPriority] || 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
};

const getStatusClass = (status) => {
    const classes = {
        'open': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        'triage_completed': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300',
        'assigned': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
        'under_investigation': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        'investigation_completed': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-300',
        'correction_in_progress': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        'closed': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    };
    return classes[status] || 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};
</script>
